import { useState } from 'react';
import styles from './Thumb.module.css';

/**
 * A product's picture. Books carry a cover URL in `image_store.url`; anything
 * without one (or whose cover fails to load) falls back to the prototype's
 * first-letter tile on the category tint, so the grid never shows a broken image.
 *
 * `fit` picks how the cover fills the tile:
 *  - "cover" (default) crops to fill — right for grid tiles, which must line up.
 *  - "contain" shows the whole cover, centred on the tint. Source covers range
 *    from 128px thumbnails to 4K photos, so a small one is allowed to grow only
 *    to MAX_UPSCALE of its natural size — enough to fill most of a hero tile
 *    without turning a thumbnail into mush.
 */

/** How far a cover may be enlarged past its natural size before it looks soft. */
const MAX_UPSCALE = 2.5;

export function Thumb({
  letter,
  tint,
  deep,
  src,
  alt,
  fit = 'cover',
  size = 'md',
  className,
  children,
  ...rest
}) {
  const [failed, setFailed] = useState(false);
  const [natural, setNatural] = useState(null);

  // Both pieces of state describe one particular picture, so a tile whose `src`
  // changes has to forget them. The book detail page swaps a chapter preview
  // into the cover tile; without this, a cover that failed to load would blank
  // every preview shown after it, and a small cover's upscale cap would go on
  // constraining a larger image that replaced it.
  const [shown, setShown] = useState(src);
  if (shown !== src) {
    setShown(src);
    setFailed(false);
    setNatural(null);
  }

  const showImage = Boolean(src) && !failed;

  // Only meaningful for "contain": bound the rendered size to both the tile and
  // the cover's own resolution, whichever is smaller.
  const upscaleCap =
    fit === 'contain' && natural
      ? {
          maxWidth: `min(100%, ${Math.round(natural.w * MAX_UPSCALE)}px)`,
          maxHeight: `min(100%, ${Math.round(natural.h * MAX_UPSCALE)}px)`,
        }
      : undefined;

  return (
    <div
      className={`${styles.thumb} ${styles[size]} ${className ?? ''}`}
      style={{ background: tint }}
      {...rest}
    >
      {showImage ? (
        <img
          className={`${styles.image} ${styles[fit]}`}
          src={src}
          alt={alt ?? ''}
          loading="lazy"
          decoding="async"
          style={upscaleCap}
          onLoad={(e) =>
            setNatural({ w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight })
          }
          onError={() => setFailed(true)}
        />
      ) : (
        <span className={styles.letter} style={{ color: deep }} aria-hidden="true">
          {letter}
        </span>
      )}
      {children}
    </div>
  );
}
